import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, Activity, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

const Login = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        toast.success('Welcome back!');
      } else if (result.requiresSSO) {
        // User requires SSO authentication - redirect to SAML login
        toast('Redirecting to Single Sign-On...', {
          icon: 'ℹ️',
        });
        window.location.href = result.redirectUrl;
        return;
      } else {
        toast.error(result.error || result.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      <div className="flex min-h-screen">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-10" />
          <div className="relative z-10 flex flex-col justify-center px-12 text-white">
            <div className="flex items-center mb-8">
              <Activity className="w-12 h-12 mr-4" />
              <h1 className="text-3xl font-bold">Cursor Analytics</h1>
            </div>
            
            <h2 className="text-4xl font-bold mb-6 leading-tight">
              Unlock insights from your 
              <br />
              <span className="text-blue-200">Cursor AI usage</span>
            </h2>
            
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">
              Analyze team performance, track feature adoption, and optimize 
              your development workflow with comprehensive analytics.
            </p>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-200 rounded-full mr-3" />
                <span className="text-blue-100">Instant upload with usage tracking</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-200 rounded-full mr-3" />
                <span className="text-blue-100">Team performance insights</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-200 rounded-full mr-3" />
                <span className="text-blue-100">Feature adoption analytics</span>
              </div>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white bg-opacity-10 rounded-full -translate-y-32 translate-x-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white bg-opacity-5 rounded-full translate-y-24 -translate-x-24" />
        </div>

        {/* Right side - Login form */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center justify-center mb-8">
              <Activity className="w-8 h-8 text-primary-600 mr-3" />
              <h1 className="text-2xl font-bold text-secondary-900">Cursor Analytics</h1>
            </div>

            <div className="card">
              <div className="card-body p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                    Welcome back
                  </h2>
                  <p className="text-secondary-600">
                    Sign in to your analytics dashboard
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`input ${errors.email ? 'input-error' : ''}`}
                      placeholder="Enter your email"
                      disabled={loading}
                    />
                    {errors.email && (
                      <p className="form-error">{errors.email}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`input pr-10 ${errors.password ? 'input-error' : ''}`}
                        placeholder="Enter your password"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary-400 hover:text-secondary-600"
                        disabled={loading}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="form-error">{errors.password}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full group"
                  >
                    {loading ? (
                      <LoadingSpinner size="small" />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-sm text-secondary-500">
                Professional Analytics Dashboard for Development Teams
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 